import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  Avatar,
  Badge,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  MoreOptions,
  ProgressBar,
  RichTextValue
} from 'components/common';
import { Box, BoxOverflowWrapper, BoxRow } from 'components/common/box';
import {
  AchievementTemplateAddItem,
  AchievementTemplateDeleteItems,
  AchievementTemplateImport,
  AchievementTemplateOrder,
  AchievementTemplateSettings,
  AchievementTemplateUpdateItem
} from '.';

import type { GetAchievementTemplateItemsRes } from 'shared-types/responses';

interface Props {
  addItemText: string;
  addItemSuccessMessage: string;
  deleteAllTitle: string;
  deleteAllText: string;
  deleteAllSuccessMessage: string;
  deleteItemSuccessMessage: string;
  deleteItemText: string;
  deleteItemTitle: string;
  editItemTitle: string;
  editItemSuccessMessage: string;
  itemAnswerableByUserLabel: string;
  itemLabel: string;
  itemMarkableByUserLabel: string;
  noItemsTitle: string;
  title: string;
}

const AchievementTemplate = ({
  addItemText,
  addItemSuccessMessage,
  deleteAllTitle,
  deleteAllText,
  deleteAllSuccessMessage,
  deleteItemSuccessMessage,
  deleteItemText,
  deleteItemTitle,
  editItemTitle,
  editItemSuccessMessage,
  itemAnswerableByUserLabel,
  itemLabel,
  itemMarkableByUserLabel,
  noItemsTitle,
  title
}: Props) => {
  const {
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  const { data, loading, updateData } =
    useApiData<GetAchievementTemplateItemsRes>({
      url: `/admin/achievements/${achievement.id}/template?includeProgressData=true`
    });

  const items = data?.template || [];

  return (
    <div className="items">
      <Box
        headerContent={
          <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
            <AchievementTemplateImport
              hasExistingItems={Boolean(items.length)}
              updateTemplate={updateData}
            />

            <AchievementTemplateAddItem
              achievementId={achievement.id}
              buttonText={addItemText}
              itemAnswerableByUserLabel={itemAnswerableByUserLabel}
              itemLabel={itemLabel}
              itemMarkableByUserLabel={itemMarkableByUserLabel}
              numberOfItems={items.length}
              successMessage={addItemSuccessMessage}
              updateTemplate={updateData}
            />

            <AchievementTemplateOrder
              items={items}
              updateTemplate={updateData}
            />

            <MoreOptions category="lighter" styleContentButtons={false}>
              <AchievementTemplateDeleteItems
                achievementId={achievement.id}
                buttonText={messages.commonDeleteAll}
                confirmText={deleteAllText}
                confirmTitle={deleteAllTitle}
                successMessage={deleteAllSuccessMessage}
                updateTemplate={updateData}
              />
            </MoreOptions>
          </FlexContainer>
        }
        headerContentWrap={true}
        padding={0}
        title={title}
        titleDescription={
          <FlexContainer style={{ gap: 10, flexWrap: 'wrap' }}>
            <div>
              {achievement.isTemplateShareable
                ? messages.achievementTemplateShared
                : messages.achievementTemplatePrivate}
            </div>

            <AchievementTemplateSettings />
          </FlexContainer>
        }
      >
        {items.length ? (
          <BoxOverflowWrapper>
            <div className="items-wrapper">
              {items.map((item, i) => (
                <BoxRow key={item.id}>
                  <div className="item">
                    <div className="item-content">
                      <div className="item-number">
                        <Avatar fontSize="1.4rem" padding="2px 0" size="24px">
                          {i + 1}
                        </Avatar>
                      </div>

                      <div className="item-text">
                        <RichTextValue html={item.text} />
                      </div>
                    </div>

                    <div className="badges">
                      {item.isAnswerableByUser ? (
                        <div className="item-text-access">
                          <Badge
                            size="small"
                            text={messages.commonUserCanAnswer}
                          />
                        </div>
                      ) : null}

                      {item.isMarkableByUser ? (
                        <div className="item-text-access">
                          <Badge
                            size="small"
                            text={messages.commonUserCanMarkAsComplete}
                          />
                        </div>
                      ) : null}
                    </div>

                    <div className="item-progress">
                      {loading ? (
                        <div className="stats" style={{ marginRight: 80 }}>
                          <LoadingIndicator
                            padding={0}
                            size={20}
                            style={{ position: 'relative', top: 2 }}
                          />
                        </div>
                      ) : (
                        <div>
                          {item.progressData?.numberOfActiveCompletions ? (
                            <ProgressBar
                              label={`${item.progressData.numberOfCompletedItems}/${item.progressData.numberOfActiveCompletions} ${messages.tasksNumberOfAnswers}`}
                              color={theme.successColor}
                              progress={
                                item.progressData.numberOfCompletedItems /
                                item.progressData.numberOfActiveCompletions
                              }
                              style={{ minWidth: 100, width: 'fit-content' }}
                            />
                          ) : (
                            <div className="item-progress-empty">
                              {messages.commonNoCompletions}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="item-actions">
                      <MoreOptions
                        renderAsFixed={true}
                        styleContentButtons={false}
                      >
                        <AchievementTemplateUpdateItem
                          achievementId={achievement.id}
                          item={{ ...item, order: i + 1 }}
                          itemAnswerableByUserLabel={itemAnswerableByUserLabel}
                          itemLabel={itemLabel}
                          itemMarkableByUserLabel={itemMarkableByUserLabel}
                          modalTitle={editItemTitle}
                          numberOfItems={items.length}
                          successMessage={editItemSuccessMessage}
                          updateTemplate={updateData}
                        />

                        <AchievementTemplateDeleteItems
                          achievementId={achievement.id}
                          buttonText={messages.commonDelete}
                          confirmTitle={deleteItemTitle}
                          confirmText={deleteItemText}
                          itemId={item.id}
                          successMessage={deleteItemSuccessMessage}
                          updateTemplate={updateData}
                        />
                      </MoreOptions>
                    </div>
                  </div>
                </BoxRow>
              ))}
            </div>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="list" title={noItemsTitle} />
        )}
      </Box>

      <style jsx>{`
        .items-wrapper {
          display: table;
          width: 100%;
        }
        .item {
          align-items: flex-start;
          display: flex;
          gap: 30px;
          padding: 5px 0;
        }
        .item-content {
          align-items: flex-start;
          display: flex;
          flex-grow: 1;
          gap: 10px;
        }
        .item-text {
          color: ${theme.textColorLight};
          flex-grow: 1;
          font-size: 1.5rem;
          font-family: inherit;
          font-weight: 600;
          line-height: 1.4;
          min-width: 200px;
          padding: 1px 15px 0px 5px;
        }
        .badges {
          display: flex;
          flex-direction: row;
        }
        .item-text-access {
          margin-right: 10px;
          padding: 1px 0;
        }
        .item-progress {
          margin-right: 10px;
        }
        .item-progress-empty {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1;
          padding: 5px 0;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
};

export default AchievementTemplate;
